/**
 * Pure async function that runs one `/agent-action` exchange against the
 * backend. Streams the NDJSON-over-SSE response and dispatches the events
 * the OOXML recovery flow needs (document body / `execute_js` requests
 * routed back via the caller-supplied handlers). Returns the agent's
 * accumulated text reply.
 *
 * Extracted from `ChatContext` so the streaming/event-loop logic doesn't
 * crowd the React context. All React-side wiring (auth header, tool
 * handlers, error reporting) is injected via `args`.
 */

import { z } from "zod";

import { parseStreamedTimestamp } from "@common/components/chat/parseStreamedTimestamp";

import { agentActionStream } from "./api";

const StreamedText = z.string().nullish().transform(value => value ?? undefined);

const AgentActionStreamEvent = z.object({
    type: z.string(),
    request_type: StreamedText,
    request_id: StreamedText,
    code: StreamedText,
    role: StreamedText,
    tool_call_id: StreamedText,
    turn_id: StreamedText,
    timestamp: z.unknown(),
    text: StreamedText,
});

export type AgentScriptOrigin = "agent_chat" | "redline_recovery";

export interface JsExecutionRequest {
    requestId: string | undefined;
    code: string | undefined;
    agentRole: string | undefined;
    toolCallId: string | undefined;
    turnId: string | undefined;
    requestedAt: string | undefined;
    scriptOrigin: AgentScriptOrigin;
    rejectReason: string | null;
    onScriptFinished?: () => Promise<void>;
}

export interface AgentActionOptions {
    onScriptFinished?: () => Promise<void>;
    rejectJsExecutionReason?: () => string | null;
}

export interface RunAgentActionStreamArgs {
    dioptraFetchStream: (url: string, options?: RequestInit) => Promise<Response>;
    onAddinContentRequest: (requestId: string | undefined) => void;
    onJsExecutionRequest: (args: JsExecutionRequest) => Promise<void>;
    prompt: string;
    toolNames: string[];
    modelName?: string;
    options?: AgentActionOptions;
}

export async function runAgentActionStream({
    dioptraFetchStream,
    onAddinContentRequest,
    onJsExecutionRequest,
    prompt,
    toolNames,
    modelName,
    options,
}: RunAgentActionStreamArgs): Promise<string> {
    const stream = await agentActionStream({ dioptraFetchStream, prompt, toolNames, modelName });
    const reader = stream.getReader();
    const decoder = new TextDecoder();

    let buffer = "";
    let summary = "";
    let streamError: Error | null = null;

    const handleEvent = async (rawEvent: unknown): Promise<void> => {
        const parsed = AgentActionStreamEvent.safeParse(rawEvent);
        if (!parsed.success) return;
        const event = parsed.data;
        if (event.type === "addin_content_request" && event.request_type === "document") {
            onAddinContentRequest(event.request_id);
        } else if (event.type === "addin_content_request" && event.request_type === "js_execution") {
            await onJsExecutionRequest({
                requestId: event.request_id,
                code: event.code,
                agentRole: event.role,
                toolCallId: event.tool_call_id,
                turnId: event.turn_id,
                requestedAt: parseStreamedTimestamp(event.timestamp),
                scriptOrigin: "redline_recovery",
                rejectReason: options?.rejectJsExecutionReason?.() ?? null,
                onScriptFinished: options?.onScriptFinished,
            });
        } else if (event.type === "text" && event.text !== undefined) {
            summary += event.text;
        } else if (event.type === "error") {
            streamError = new Error(event.text || "Agent action stream error");
        }
    };

    try {
        // eslint-disable-next-line no-constant-condition
        while (true) {
            const { done, value } = await reader.read();
            if (done) {
                if (buffer) {
                    let trailingEvent: unknown;
                    try { trailingEvent = JSON.parse(buffer); } catch { break; }
                    await handleEvent(trailingEvent);
                }
                break;
            }
            buffer += decoder.decode(value);
            let newlineIndex;
            while ((newlineIndex = buffer.indexOf("\n")) !== -1) {
                const line = buffer.slice(0, newlineIndex);
                buffer = buffer.slice(newlineIndex + 1);
                if (!line) continue;
                let event: unknown;
                try {
                    event = JSON.parse(line);
                } catch (e) {
                    console.warn("[runAgentActionStream] failed to parse line:", line, e);
                    continue;
                }
                await handleEvent(event);
            }
        }
    } finally {
        reader.cancel().catch(() => {});
    }

    if (streamError) throw streamError;
    return summary;
}
